import {
  diffBomCorrectionDrafts,
  summarizeBomCorrectionDiff,
  type BomLineDiffRow,
} from "@/lib/manufacturing/bomCorrectionDiff";
import type { DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";

const badgeClass: Record<BomLineDiffRow["kind"], string> = {
  changed:
    "rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  same: "rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600 dark:bg-zinc-800 dark:text-zinc-300",
  added:
    "rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
  removed:
    "rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-800 dark:bg-red-900/40 dark:text-red-200",
};

const badgeLabel: Record<BomLineDiffRow["kind"], string> = {
  changed: "Replace",
  same: "Unchanged",
  added: "Add",
  removed: "Remove",
};

/** Full-replace BOM correction summary: before → after per line. */
export function ManufacturingBomCorrectionDiffPanel({
  prior,
  next,
}: {
  prior: DictatedRecipeDraft;
  next: DictatedRecipeDraft;
}) {
  const rows = diffBomCorrectionDrafts(prior, next);
  const summary = summarizeBomCorrectionDiff(rows);

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/80 px-3 py-2 text-xs text-violet-950 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100">
      <div className="font-semibold">Proposed BOM (full replace of material lines)</div>
      <p className="mt-1 text-[11px] opacity-90">
        Saving replaces the recipe’s material lines with this entire list. Nested component /
        part-use lines stay as-is.{" "}
        <strong>{summary.changed}</strong> replace · <strong>{summary.same}</strong> unchanged ·{" "}
        <strong>{summary.added}</strong> add · <strong>{summary.removed}</strong> remove.
      </p>
      <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
        {rows.map((row) => {
          const line = row.next ?? row.prior;
          if (!line) return null;
          return (
            <li
              key={row.key}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-violet-100/80 py-1 last:border-0 dark:border-violet-900/50"
            >
              <span className={badgeClass[row.kind]}>{badgeLabel[row.kind]}</span>
              <span className="font-medium">
                {line.material_name_heard}
                {line.slot_label ? (
                  <span className="font-normal text-violet-800/80 dark:text-violet-200/80">
                    {" "}
                    · <code>{line.slot_label}</code>
                  </span>
                ) : null}
              </span>
              {row.kind === "changed" && row.prior && row.next ? (
                <span className="tabular-nums">
                  qty {row.prior.quantity} → <strong>{row.next.quantity}</strong>
                </span>
              ) : row.kind === "added" && row.next ? (
                <span className="tabular-nums">
                  qty <strong>{row.next.quantity}</strong>
                </span>
              ) : row.kind === "removed" && row.prior ? (
                <span className="tabular-nums line-through opacity-70">
                  qty {row.prior.quantity}
                </span>
              ) : row.next ? (
                <span className="tabular-nums opacity-70">qty {row.next.quantity}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
